import { useState, useEffect } from "react";
import { BarChart3, TrendingUp, Users, Boxes, ShoppingCart, Wallet, Target, PieChart } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart as RPie, Pie, Cell, Legend } from "recharts";
import api, { money, errMsg } from "../lib/api";
import DatePicker from "../components/ui/DatePicker";
import { todayIST, firstOfMonthIST } from "../lib/datetime";
import useAutoRefresh from "../lib/useAutoRefresh";

const role = () => {
  try {
    return (JSON.parse(localStorage.getItem("lss_user") || "{}").role || "owner").toLowerCase();
  } catch {
    return "owner";
  }
};

const COLORS = ["#D4AF37", "#0F766E", "#1D4ED8", "#BE123C", "#7C3AED", "#EA580C", "#475569", "#059669"];

export default function Analysis() {
  const isOwner = role() === "owner";
  // Delhi-local defaults — `toISOString()` would resolve to yesterday's date
  // for any shift running between midnight and 05:30 IST.
  const [from, setFrom] = useState(firstOfMonthIST());
  const [to, setTo] = useState(todayIST());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [targets, setTargets] = useState({});
  const [savingT, setSavingT] = useState(false);

  // `silent` keeps the screen still — the poll repaints the MIS figures in
  // place instead of blanking them to a spinner. It also leaves `targets`
  // alone: this screen edits staff targets inline, and a background fetch
  // overwriting them would destroy whatever an owner is halfway through
  // typing. The server wins again on an explicit date change.
  const fetchMis = async (f, t, silent = false) => {
    if (!f || !t || f > t) {
      setData(null);
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    try {
      const res = await api.get(`/analysis/mis?date_from=${f}&date_to=${t}`);
      setData(res.data);
      if (silent) return;
      const nt = {};
      for (const s of res.data?.staff || []) {
        nt[s.staff_id] = { service: s.target_service || "", retail: s.target_retail || "", card: s.target_cards || "" };
      }
      setTargets(nt);
    } catch (e) {
      if (!silent) toast.error(errMsg(e));
      else console.error(e);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { fetchMis(from, to); }, [from, to]);
  // Sales, punches and target progress move all day — no refresh button here.
  useAutoRefresh(() => fetchMis(from, to, true), 30000);

  const saveTargets = async () => {
    const payload = {};
    for (const [sid, t] of Object.entries(targets)) {
      payload[sid] = { service: Number(t.service) || 0, retail: Number(t.retail) || 0, card: Number(t.card) || 0 };
    }
    setSavingT(true);
    try {
      await api.post("/analysis/targets", { targets: payload });
      toast.success("Staff targets saved");
      fetchMis(from, to);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingT(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="h-96 bg-slate-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  const s = data?.sales || {};
  const p = data?.profit || {};
  const c = data?.costing || {};
  const b = data?.procurement_budget || {};
  const ex = data?.expenses || {};

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-950 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-amber-700" />
            Analysis & MIS
          </h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">
            Sales • Costing • Staff • Inventory usage • Expenses • Profitability — {data?.month}{data?.prev_month ? ` vs ${data.prev_month}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap md:justify-end">
          <div className="flex items-end gap-3">
            <DatePicker label="From" value={from} onChange={(e) => setFrom(e.target.value)} testId="mis-from" />
            <span className="text-slate-400 font-bold pb-2">→</span>
            <DatePicker label="To" value={to} onChange={(e) => setTo(e.target.value)} testId="mis-to" />
          </div>
        </div>
      </div>

      {(!from || !to || from > to) && (
        <div className="bg-amber-50 border border-amber-300 rounded-2xl px-6 py-4 text-sm font-bold text-amber-900 text-center">
          Select the correct date range (From date must be on or before To date).
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="lss-card p-4"><span className="lss-overline block">Revenue (post-disc)</span><span className="text-xl font-extrabold text-slate-950 tabular">{money(s.post_discount || 0)}</span><span className={`block text-[11px] font-bold ${data?.mom_revenue_pct >= 0 ? "text-emerald-700" : "text-rose-700"}`}>MoM {data?.mom_revenue_pct ?? 0}%</span></div>
        <div className="lss-card p-4"><span className="lss-overline block">Revenue (actual rate)</span><span className="text-xl font-extrabold text-slate-700 tabular">{money(s.actual_rate || 0)}</span></div>
        <div className="lss-card p-4"><span className="lss-overline block">Material Cost</span><span className="text-xl font-extrabold text-amber-800 tabular">{money(c.total_cost || 0)}</span><span className="block text-[11px] font-bold text-slate-500">MoM {c.mom_cost_pct ?? 0}% · {c.cost_pct ?? 0}% of serviced revenue</span></div>
        <div className="lss-card p-4"><span className="lss-overline block">Opex (bank+cash)</span><span className="text-xl font-extrabold text-slate-700 tabular">{money(p.opex || 0)}</span></div>
        <div className="lss-card p-4"><span className="lss-overline block">Net Profit</span><span className={`text-xl font-extrabold tabular ${(p.net_profit ?? 0) >= 0 ? "text-emerald-800" : "text-rose-700"}`}>{money(p.net_profit || 0)}</span></div>
        <div className="lss-card p-4"><span className="lss-overline block">Net Margin</span><span className="text-xl font-extrabold text-slate-950 tabular">{p.net_margin_pct ?? 0}%</span></div>
      </div>

      {/* Sales */}
      <div className="lss-card overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/70 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2"><ShoppingCart className="w-4 h-4 text-amber-700" /> Sales Analysis</span>
          <span className="text-xs font-semibold text-slate-500">Cash {money(s.cash || 0)} · Card {money(s.card || 0)} · Other {money(s.other || 0)} · Cards sold {s.cards?.count || 0} ({money(s.cards?.value || 0)})</span>
        </div>
        <div className="grid md:grid-cols-2 gap-0">
          <div className="p-4 h-64">
            <div className="lss-overline mb-2 px-2">Daily revenue</div>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={s.by_day || []}>
                <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={Math.max(0, Math.floor((s.by_day || []).length / 10))} />
                <YAxis tick={{ fontSize: 9 }} width={60} />
                <Tooltip formatter={(v) => money(v)} />
                <Bar dataKey="revenue" fill="#D4AF37" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="p-4 h-64">
            <div className="lss-overline mb-2 px-2">By service / product type</div>
            <ResponsiveContainer width="100%" height="90%">
              <RPie>
                <Pie data={s.by_type || []} dataKey="revenue" nameKey="type" outerRadius={80} label={{ fontSize: 10 }}>
                  {(s.by_type || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => money(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </RPie>
            </ResponsiveContainer>
          </div>
        </div>
        {(s.top_clients || []).length > 0 && (
          <div className="px-6 py-3 border-t border-slate-200 text-xs text-slate-600 font-medium">
            Top customers: {(s.top_clients || []).slice(0, 5).map((c) => `${c.client} (${money(c.revenue)})`).join(" · ")}
          </div>
        )}
      </div>

      {/* Staff */}
      <div className="lss-card overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/70 flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2"><Users className="w-4 h-4 text-amber-700" /> Staff Analysis — services, products, cards, incentives, salary, targets</span>
          {isOwner && <button onClick={saveTargets} disabled={savingT} className="lss-btn-gold px-4 py-1.5 text-xs disabled:opacity-50">{savingT ? "Saving..." : "Save Targets ✓"}</button>}
        </div>
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
          <table className="lss-table min-w-[1080px]">
            <thead>
              <tr>
                <th>Staff</th>
                <th className="text-right">Services</th>
                <th className="text-right">Svc Revenue</th>
                <th className="text-right">Products ₹</th>
                <th className="text-right">Cards</th>
                <th className="text-right">Incentive ₹</th>
                <th className="text-right">Salary ₹</th>
                <th className="text-right">Tgt Svc ₹</th>
                <th className="text-right">Achv %</th>
              </tr>
            </thead>
            <tbody>
              {(data?.staff || []).map((r) => (
                <tr key={r.staff_id} className="hover:bg-slate-50/80">
                  <td className="font-bold text-xs">{r.staff_name}</td>
                  <td className="text-right tabular">{r.services_done}</td>
                  <td className="text-right tabular font-semibold">{money(r.service_revenue)}</td>
                  <td className="text-right tabular">{money(r.products_sold)}</td>
                  <td className="text-right tabular">{r.cards_sold}</td>
                  <td className="text-right tabular text-emerald-800 font-bold">{money(r.incentives_earned)}</td>
                  <td className="text-right tabular">{money(r.salary_payable)}</td>
                  <td className="text-right">
                    {isOwner ? (
                      <input
                        type="number" min="0"
                        value={targets[r.staff_id]?.service ?? ""}
                        onChange={(e) => setTargets((prev) => ({ ...prev, [r.staff_id]: { ...(prev[r.staff_id] || {}), service: e.target.value } }))}
                        placeholder="—"
                        className="lss-input w-24 text-right px-2 py-1 text-xs tabular"
                      />
                    ) : (
                      <span className="tabular">{r.target_service ? money(r.target_service) : "—"}</span>
                    )}
                  </td>
                  <td className="text-right">
                    {r.ach_service_pct == null ? (
                      <span className="text-slate-300">—</span>
                    ) : (
                      <span className={`font-extrabold tabular ${r.ach_service_pct >= 100 ? "text-emerald-800" : r.ach_service_pct >= 70 ? "text-amber-800" : "text-rose-700"}`}>
                        {r.ach_service_pct}%
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-2.5 border-t border-slate-200 text-xs text-slate-500 bg-slate-50/50 font-medium">
          Targets drive achievement % — use them to align incentives (raise slabs for under-target staff, bonuses past 100%).
        </div>
      </div>

      {/* Inventory usage + costing */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="lss-card overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/70">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2"><Boxes className="w-4 h-4 text-amber-700" /> Top Consumed Products (cost ₹{Number(c.total_cost || 0).toLocaleString("en-IN")})</span>
          </div>
          <div className="overflow-auto max-h-[380px]">
            <table className="lss-table">
              <thead><tr><th>Product</th><th className="text-right">Qty</th><th className="text-right">Cost ₹</th></tr></thead>
              <tbody>
                {(data?.inventory_usage || []).slice(0, 15).map((u, i) => (
                  <tr key={i} className="hover:bg-slate-50/80">
                    <td className="text-xs font-semibold max-w-[240px] truncate" title={u.name}>{u.name}</td>
                    <td className="text-right tabular">{u.qty}</td>
                    <td className="text-right tabular font-bold">{money(u.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="lss-card overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/70">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-amber-700" /> Weekly Cost vs MoM ({money(c.prev_cost || 0)} → {money(c.total_cost || 0)}, {c.mom_cost_pct ?? 0}%)</span>
          </div>
          <div className="p-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={c.weekly || []}>
                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 9 }} width={60} />
                <Tooltip formatter={(v) => money(v)} />
                <Bar dataKey="cost" fill="#0F766E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Procurement budget */}
      <div className="lss-card overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/70 flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2"><Target className="w-4 h-4 text-amber-700" /> Next-15-Day Purchase Budget — {money(b.total_est_cost || 0)} ({b.total_items || 0} items)</span>
          <span className="text-xs text-slate-500 font-medium">Daily-use rate × 15 days − on-hand stock</span>
        </div>
        <div className="overflow-auto max-h-[380px]">
          <table className="lss-table min-w-[760px]">
            <thead><tr><th>Product</th><th className="text-right">Daily Use</th><th className="text-right">Need 15d</th><th className="text-right">On Hand</th><th className="text-right">Buy Qty</th><th className="text-right">Est. Cost ₹</th></tr></thead>
            <tbody>
              {(b.items || []).map((it) => (
                <tr key={it.sku_id} className="hover:bg-slate-50/80">
                  <td className="text-xs font-semibold max-w-[260px] truncate" title={it.name}>{it.name}</td>
                  <td className="text-right tabular">{it.daily_use}</td>
                  <td className="text-right tabular">{it.need_15d}</td>
                  <td className="text-right tabular">{it.on_hand}</td>
                  <td className="text-right tabular font-extrabold text-amber-800">{it.buy_qty}</td>
                  <td className="text-right tabular font-bold">{money(it.est_cost)}</td>
                </tr>
              ))}
              {(b.items || []).length === 0 && (
                <tr><td colSpan={6} className="text-center text-slate-500 font-medium py-8 text-xs">Stock covers the next 15 days — nothing to buy.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expenses + Profit */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="lss-card overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/70">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2"><Wallet className="w-4 h-4 text-amber-700" /> Expense Analysis — Bank + Cash ({money(data?.expenses?.total_opex || 0)})</span>
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-xs font-semibold text-slate-600">
              <span>Month POS cash: <b className="tabular">{money(data?.expenses?.month_cash_pos || 0)}</b></span>
              <span>Bank cash deposits (T+1): <b className="tabular text-sky-900">{money(data?.expenses?.month_bank_cash_t1 || 0)}</b></span>
            </div>
          </div>
          <div className="overflow-auto max-h-[320px]">
            <table className="lss-table">
              <thead><tr><th>Ledger</th><th className="text-right">Amount ₹</th></tr></thead>
              <tbody>
                {(data?.expenses?.by_ledger || []).map((e, i) => (
                  <tr key={i} className="hover:bg-slate-50/80">
                    <td className="text-xs font-semibold">{e.ledger}</td>
                    <td className="text-right tabular font-bold">{money(e.amount)}</td>
                  </tr>
                ))}
                {(data?.expenses?.by_ledger || []).length === 0 && (
                  <tr><td colSpan={2} className="text-center text-slate-500 font-medium py-8 text-xs">No approved book expenses this month.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="lss-card overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/70">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2"><PieChart className="w-4 h-4 text-amber-700" /> Profitability Analysis</span>
          </div>
          <div className="p-6 space-y-3 text-sm font-semibold">
            {[
              ["Revenue", p.revenue || 0, "text-slate-950"],
              ["− Material Cost (COGS)", p.cogs || 0, "text-amber-800"],
              ["= Gross Profit", p.gross_profit || 0, "text-sky-900"],
              ["− Operating Overhead", p.opex || 0, "text-slate-700"],
            ].map(([label, val, cls]) => (
              <div key={label} className="flex justify-between border-b border-slate-100 pb-2">
                <span className="text-slate-600">{label}</span>
                <span className={`tabular ${cls}`}>{money(val)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-1">
              <span className="font-extrabold text-slate-950">= Net Profit ({p.net_margin_pct ?? 0}%)</span>
              <span className={`text-xl font-extrabold tabular ${(p.net_profit ?? 0) >= 0 ? "text-emerald-800" : "text-rose-700"}`}>{money(p.net_profit || 0)}</span>
            </div>
            <div className="text-xs text-slate-500 font-medium">Gross margin {p.gross_margin_pct ?? 0}% · Revenue = POS post-discount + approved bank/cash income</div>
          </div>
        </div>
      </div>
    </div>
  );
}
